package com.evoapps.evoshift.domain.usecase.reports

import com.evoapps.evoshift.domain.model.AccountantExport
import com.evoapps.evoshift.domain.model.AccountantExportFormat
import com.evoapps.evoshift.domain.model.InvalidEmailException
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.provider.DeviceInfoProvider
import com.evoapps.evoshift.domain.repository.AccountantExportRepository

/**
 * SH-117: a roster month's working time for the accountant, built by the server (exportForAccountant)
 * in the app's language: downloaded through a 24-hour link, or also e-mailed to [emailTo]. The
 * owner's and financial managers' (the server checks); never the pay, which the accountant's
 * payroll software calculates from these hours.
 */
class ExportForAccountantUseCase(
    private val repository: AccountantExportRepository,
    private val deviceInfo: DeviceInfoProvider
) {
    suspend operator fun invoke(
        rosterId: String,
        format: AccountantExportFormat,
        emailTo: String? = null
    ): Result<AccountantExport> {
        val (year, month) = runCatching { RosterIds.yearMonthOf(rosterId) }.getOrNull()
            ?: return Result.failure(IllegalArgumentException("Not a roster id: $rosterId"))
        val address = emailTo?.trim()?.takeIf { it.isNotEmpty() }
        if (address != null && !isEmailAddress(address)) return Result.failure(InvalidEmailException())
        return repository.export(RosterIds.workplaceIdOf(rosterId), year, month, format, deviceInfo.getLanguage(), address)
    }

    companion object {
        /** The server's check (functions/accountantExport.js validEmail): one "@", a dotted domain, no spaces. */
        fun isEmailAddress(value: String): Boolean {
            val email = value.trim()
            if (email.length !in 6..254) return false
            if (email.any { it <= ' ' || it in ",;<>" }) return false
            val at = email.indexOf('@')
            if (at < 1 || at != email.lastIndexOf('@')) return false
            val domain = email.substring(at + 1)
            val dot = domain.lastIndexOf('.')
            return dot > 0 && dot < domain.length - 2 && ".." !in domain
        }
    }
}
